import { motion, AnimatePresence } from "framer-motion";
import { FaCubes, FaLeaf } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectCurrentBuilder,
  setResultsTab,
  toggleTabsOpen,
} from "../../app/features/builder/builderSlice";
import TranslatableText from "../translations/TranslatableText";
import { TranslationKey } from "../../types/modulesInterfaces";
import { selectProjectAreaDefined } from "@app/features/project/projectSlice";
import { useEffect } from "react";

const tabClass = (selected: boolean, extra = "") =>
  `bmt-results-tab clickable fs-13 ${selected ? "bmt-results-tab-selected" : "bmt-results-tab-unselected"} ${extra}`.trim();

const iconColor = (selected: boolean) => selected ? "var(--bintact-bright)" : "var(--icon)"

const ComparisonToggle = ({
  tabsOpen,
  onToggle,
  labelKey,
  labelClassName = "px-1 fs-13 no-wrap",
}: {
  tabsOpen: boolean;
  onToggle: () => void;
  labelKey: TranslationKey;
  labelClassName?: string;
}) => (
  <>
    <div className="bmt-toggle-t2 h-100">
      <SwitchButton isOn={!tabsOpen} toggleIsOn={onToggle} />
    </div>
    <motion.div
      className="bmt-t2 clickable h-100"
      onClick={onToggle}
      whileHover={{ opacity: 0.95 }}
    >
      <TranslatableText className={labelClassName} translationKey={labelKey} />
    </motion.div>
  </>
);

const BuilderMainTabs = ({ isInResults }: { isInResults?: boolean }) => {
  const { tabsOpen, resultsTab } = useAppSelector(selectCurrentBuilder);
  const dispatch = useAppDispatch();
  const onToggle = () => dispatch(toggleTabsOpen());
  const projectAreaDefined = useAppSelector(selectProjectAreaDefined);

  useEffect(() => {
    if (projectAreaDefined !== null && !projectAreaDefined) {
      dispatch(setResultsTab("ghg"));
    }
  }, [projectAreaDefined, dispatch])

  if (isInResults) {
    return (
      <div className="builder-main-tabs pe-4">
        <div className="bmt-container">
          <div
            className={tabClass(resultsTab === "ghg")}
            onClick={() => dispatch(setResultsTab("ghg"))}
          >
            <FaCubes className="ms-1" color={iconColor(resultsTab === "ghg")} size={16} />
            <TranslatableText className="no-wrap px-1" translationKey="project.ghg_results" />
            <AnimatePresence>
              {resultsTab === "ghg" && (
                <motion.div layout className="d-flex align-items-center h-100">
                  <ComparisonToggle
                    tabsOpen={tabsOpen}
                    onToggle={onToggle}
                    labelKey="project.project_comparison"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {projectAreaDefined && (
            <div
              className={tabClass(resultsTab === "biodiversity", "px-1")}
              onClick={() => dispatch(setResultsTab("biodiversity"))}
            >
              <FaLeaf color={iconColor(resultsTab === "biodiversity")} size={16} />
              <TranslatableText className="no-wrap ps-1" translationKey="project.biodiversity_results" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="builder-main-tabs pe-4">
      <div className="bmt-container">
        <div className="bmt-results-tab bmt-results-tab-selected">
          <div className="d-flex align-items-center">
            <TranslatableText className="px-2 fs-13 no-wrap" translationKey="activity.builder_assessment_editor" />
          </div>
          <ComparisonToggle
            tabsOpen={tabsOpen}
            onToggle={onToggle}
            labelKey="activity.tier_two_parameters"
            labelClassName="px-2 fs-13 no-wrap"
          />
        </div>
      </div>
    </div>
  );
};

const SwitchButton = ({
  isOn,
  toggleIsOn,
}: {
  isOn: boolean;
  toggleIsOn: () => void;
}) => {
  return (
    <div
      className="bmt-switch-container"
      onClick={() => toggleIsOn()}
      style={{ justifyContent: isOn ? "flex-end" : "flex-start" }}
    >
      <motion.div layout className="bmt-switch-handle">
        <AnimatePresence initial={false}>
          <motion.i
            className={`icon far fa-${isOn ? "moon" : "sun"}`}
            key={isOn ? "moon" : "sun"}
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default BuilderMainTabs;
